import { Skeleton, SkeletonSpan } from '@/components/ui/skeleton';

/**
 * Skeleton for form labels
 */
export const LabelSkeleton = ({ className = 'h-4 w-20' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for form inputs
 */
export const InputSkeleton = ({ className = 'h-10 w-full' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for form descriptions
 */
export const DescriptionSkeleton = ({ className = 'h-3 w-48' }: { className?: string }) => (
  <SkeletonSpan className={className} />
);

/**
 * Skeleton for buttons
 */
export const ButtonSkeleton = ({ className = 'h-8 w-20' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for text content
 */
export const TextSkeleton = ({ className = 'h-4 w-24' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for file upload areas
 */
export const FileUploadSkeleton = ({ className = 'h-32 w-full' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for switch/toggle elements
 */
export const SwitchSkeleton = ({ className = 'h-6 w-11' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for icons
 */
export const IconSkeleton = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <Skeleton className={className} />
);

/**
 * Skeleton for audience summary stats
 */
export const AudienceStatSkeleton = () => (
  <div className="flex items-center justify-between">
    <TextSkeleton className="h-4 w-16" />
    <div className="flex items-center gap-2">
      <IconSkeleton />
      <TextSkeleton className="h-4 w-8" />
    </div>
  </div>
);

/**
 * Skeleton for campaign summary items
 */
export const SummaryItemSkeleton = () => (
  <div className="mb-2 flex items-center justify-between">
    <TextSkeleton className="h-4 w-20" />
    <div className="flex items-center gap-2">
      <IconSkeleton />
      <TextSkeleton className="h-4 w-24" />
    </div>
  </div>
);

/**
 * Legacy full page loading states (kept for backward compatibility)
 */
export const CampaignLoadingState = () => {
  return (
    <div className="flex w-full max-w-5xl flex-col gap-4 rounded-xl bg-muted p-1 lg:flex-row">
      <div className="w-full rounded-xl border border-border bg-background p-4 lg:w-[500px] lg:p-8">
        {/* Campaign Name Skeleton */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <LabelSkeleton className="h-4 w-24" />
            <TextSkeleton className="h-4 w-12" />
          </div>
          <InputSkeleton />
        </div>

        {/* Audience Section Skeleton */}
        <div className="mt-6 space-y-2">
          <div className="flex items-center justify-between">
            <LabelSkeleton className="h-4 w-16" />
            <ButtonSkeleton className="h-8 w-20" />
          </div>
          <FileUploadSkeleton />
          <DescriptionSkeleton className="h-4 w-32" />
        </div>

        {/* AI Agent Section Skeleton */}
        <div className="mt-6 space-y-2">
          <LabelSkeleton className="h-4 w-20" />
          <InputSkeleton />
          <DescriptionSkeleton />
        </div>

        {/* Tags Section Skeleton */}
        <div className="mt-6 space-y-2">
          <LabelSkeleton className="h-4 w-12" />
          <InputSkeleton />
          <DescriptionSkeleton className="h-3 w-40" />
        </div>
      </div>

      {/* Audience Summary Skeleton */}
      <div className="w-full lg:w-auto">
        <div className="rounded-lg border border-border bg-background p-6">
          <TextSkeleton className="mb-4 h-6 w-32" />
          <div className="space-y-3">
            <AudienceStatSkeleton />
            <AudienceStatSkeleton />
            <AudienceStatSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Build page specific loading state
 */
export const BuildPageLoadingState = () => {
  return (
    <div className="space-y-6">
      {/* Primary Sender Section */}
      <div className="rounded-lg border border-border bg-background p-6">
        <TextSkeleton className="mb-4 h-6 w-32" />
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <LabelSkeleton className="h-4 w-20" />
              <InputSkeleton />
            </div>
            <div className="space-y-2">
              <LabelSkeleton className="h-4 w-16" />
              <InputSkeleton />
            </div>
          </div>
          <div className="space-y-2">
            <LabelSkeleton className="h-4 w-24" />
            <InputSkeleton className="h-20 w-full" />
          </div>
        </div>
      </div>

      {/* Fallback Settings Section */}
      <div className="rounded-lg border border-border bg-background p-6">
        <TextSkeleton className="mb-4 h-6 w-40" />
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <TextSkeleton className="h-4 w-48" />
              <DescriptionSkeleton className="h-3 w-64" />
            </div>
            <SwitchSkeleton />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <TextSkeleton className="h-4 w-44" />
              <DescriptionSkeleton className="h-3 w-60" />
            </div>
            <SwitchSkeleton />
          </div>
        </div>
      </div>

      {/* Fallback Senders Section */}
      <div className="rounded-lg border border-border bg-background p-6">
        <TextSkeleton className="mb-4 h-6 w-36" />
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <LabelSkeleton className="h-4 w-20" />
              <InputSkeleton />
            </div>
            <div className="space-y-2">
              <LabelSkeleton className="h-4 w-16" />
              <InputSkeleton />
            </div>
          </div>
          <div className="space-y-2">
            <LabelSkeleton className="h-4 w-24" />
            <InputSkeleton className="h-20 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
};
